import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import { TABLES } from './tables';
import { useAuth } from '../context/AuthContext';
import {
  getProfileRecommendation,
  getModuleGaps,
  getCareerSuggestions,
} from './suggestions';

// calculeaza sugestiile elevului autentificat, cu numar constant de interogari
export const useSuggestions = () => {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!profile) return;
    setLoading(true);
    setError(null);
    try {
      // modulele active
      const { data: modulesData, error: modulesErr } = await supabase
        .from(TABLES.modules)
        .select('id, cod_module, nume_module, credite_tinta')
        .eq('este_activ', true)
        .order('ordine');
      if (modulesErr) throw modulesErr;

      // tipurile de credit
      const { data: creditTypesData, error: typesErr } = await supabase
        .from(TABLES.credit_types)
        .select('id, nume_tip, categorie');
      if (typesErr) throw typesErr;
      const creditTypesMap = {};
      (creditTypesData || []).forEach((ct) => {
        creditTypesMap[ct.id] = {
          categorie: ct.categorie || ct.nume_tip?.toLowerCase() || '',
        };
      });

      // cererile validate ale elevului; numele activitatilor exclude cele deja facute
      const { data: requestsData, error: reqErr } = await supabase
        .from(TABLES.credit_requests)
        .select('id_tip_credit, id_module, credite_solicitate, credite_acordate, nume_activitate')
        .eq('id_elev', profile.id)
        .eq('status', 'validat');
      if (reqErr) throw reqErr;

      // activitatile predefinite active
      const { data: activitiesData, error: actErr } = await supabase
        .from(TABLES.predefined_activities)
        .select('id, nume_activitate, id_tip_credit, id_module, ore_recomandate')
        .eq('este_activ', true);
      if (actErr) throw actErr;

      // agregare credite pe module si categorii
      const moduleCreditsMap = {};
      (modulesData || []).forEach((mod) => {
        moduleCreditsMap[mod.id] = {
          id: mod.id,
          cod: mod.cod_module,
          nume: mod.nume_module,
          curricular: 0,
          extracurricular: 0,
          transferabil: 0,
          total: 0,
          tinta: mod.credite_tinta || 0,
        };
      });

      const categoryTotals = { curricular: 0, extracurricular: 0, transferabil: 0 };

      (requestsData || []).forEach((req) => {
        const categorie = creditTypesMap[req.id_tip_credit]?.categorie || '';
        const credite = req.credite_acordate || req.credite_solicitate || 0;
        const idModul = req.id_module;

        if (categorie === 'curricular') {
          categoryTotals.curricular += credite;
          if (idModul && moduleCreditsMap[idModul]) {
            moduleCreditsMap[idModul].curricular += credite;
            moduleCreditsMap[idModul].total += credite;
          }
        } else if (categorie === 'extracurricular') {
          categoryTotals.extracurricular += credite;
          if (idModul && moduleCreditsMap[idModul]) {
            moduleCreditsMap[idModul].extracurricular += credite;
            moduleCreditsMap[idModul].total += credite;
          }
        } else if (categorie === 'transferabil') {
          categoryTotals.transferabil += credite;
          if (idModul && moduleCreditsMap[idModul]) {
            moduleCreditsMap[idModul].transferabil += credite;
            moduleCreditsMap[idModul].total += credite;
          }
        }
      });

      const moduleCredits = Object.values(moduleCreditsMap).sort((a, b) =>
        a.cod.localeCompare(b.cod)
      );

      const targetModules = (modulesData || []).filter((m) => (m.credite_tinta || 0) > 0).length;

      // activitati deja facute si seed stabil pentru rotatie
      const doneActivities = (requestsData || [])
        .map((r) => r.nume_activitate)
        .filter(Boolean);

      setData({
        profileRec: getProfileRecommendation(profile.specializare),
        moduleGaps: getModuleGaps(moduleCredits, activitiesData || [], {
          excludeNames: doneActivities,
          seed: profile.id,
        }),
        careerSuggestions: getCareerSuggestions(getProfileRecommendation(profile.specializare), moduleCredits),
        categoryTotals,
        moduleCredits,
        hasRequests: (requestsData || []).length > 0,
        moduleCount: (modulesData || []).length,
        targetModules,
      });
    } catch (err) {
      console.error('Eroare la calcularea sugestiilor:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    load();
  }, [load]);

  return { loading, error, data, reload: load };
};
